import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SurveyQuestion } from '@/types';
import { cn } from '@/utils/cn';

// questionTypeLabels kept for future use if needed
// const questionTypeLabels: Record<string, string> = {
//   TEXT_SHORT: 'Short answer',
//   TEXT_LONG: 'Long answer',
//   SINGLE_CHOICE: 'Single choice',
//   MULTIPLE_CHOICE: 'Pick multiple',
//   HEADING: 'Heading',
//   BANNER: 'Banner',
// };

interface QuestionCardProps {
  question: SurveyQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
}

// Extract emoji from text (if present at the start)
const extractEmoji = (text: string): { emoji: string | null; cleanText: string } => {
  // Match emoji at the start of the text (Unicode emoji ranges)
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\u{1F3FB}-\u{1F3FF}|\u{1F9B0}-\u{1F9B3}|\u{200D}|\u{FE0F})+/u;
  const match = text.match(emojiRegex);
  
  if (match && match[0]) {
    return {
      emoji: match[0].trim(),
      cleanText: text.replace(emojiRegex, '').trim(),
    };
  }
  
  return { emoji: null, cleanText: text };
};

export const QuestionCard = ({ question, value, onChange }: QuestionCardProps) => {
  const maxSelections = useMemo(() => {
    if (question.type !== 'MULTIPLE_CHOICE') return undefined;
    // Check validationRules first, then metadata
    if (question.validationRules?.maxSelections) {
      return question.validationRules.maxSelections;
    }
    const metadata = question.metadata as { maxSelections?: number } | undefined;
    return metadata?.maxSelections;
  }, [question]);

  const minSelections = useMemo(() => {
    if (question.type !== 'MULTIPLE_CHOICE') return undefined;
    return question.validationRules?.minSelections ?? 0;
  }, [question]);

  // Extract emoji from question text (check text first, then heading)
  const questionText = question.text || question.heading?.heading || '';
  const headingText = question.heading?.heading || '';
  // Try to extract from the main text first, then from heading
  let { emoji, cleanText } = extractEmoji(questionText);
  if (!emoji && headingText && headingText !== questionText) {
    const headingResult = extractEmoji(headingText);
    if (headingResult.emoji) {
      emoji = headingResult.emoji;
      cleanText = headingResult.cleanText || questionText;
    }
  }
  
  // Fallback: use the original text if no emoji found
  if (!emoji) {
    cleanText = questionText || headingText || 'Question';
  }

  const renderInput = () => {
    // HEADING and BANNER types don't render inputs
    if (question.type === 'HEADING' || question.type === 'BANNER') {
      return null;
    }
    
    switch (question.type) {
      case 'TEXT_SHORT':
      case 'EMAIL':
        return (
          <input
            type={question.type === 'EMAIL' ? 'email' : 'text'}
            value={typeof value === 'string' ? (value || '') : ''}
            onChange={(event) => onChange(event.target.value)}
            placeholder={question.metadata?.placeholder as string || (question.type === 'EMAIL' ? 'your.email@example.com' : 'Type your answer')}
            maxLength={question.validationRules?.maxLength}
            minLength={question.validationRules?.minLength}
            pattern={question.validationRules?.pattern}
            className="w-full rounded-xl border-2 border-input bg-background px-4 py-3 text-base transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-0"
          />
        );
      case 'TEXT_LONG':
        // Check if this is a display-only info screen
        if (question.metadata?.displayOnly) {
          return null; // Don't render input for display-only screens
        }
        return (
          <textarea
            value={typeof value === 'string' ? (value || '') : ''}
            onChange={(event) => onChange(event.target.value)}
            placeholder={question.metadata?.placeholder as string || "Share more details"}
            rows={5}
            maxLength={question.validationRules?.maxLength}
            minLength={question.validationRules?.minLength}
            className="w-full rounded-xl border-2 border-input bg-background px-4 py-3 text-base transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-0"
          />
        );
      case 'SINGLE_CHOICE':
        return (
          <div className="grid gap-3">
            {question.options?.map((option) => {
              // Store/send the backend value (option.value) when present, otherwise fallback to option.id.
              const storedValue = option.value || option.id;
              // Always allow selection - check if current value matches (even if undefined/empty)
              const isSelected = value === storedValue || value === option.id || value === option.value;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onChange(storedValue)}
                  className={cn(
                    'group relative w-full rounded-xl border-2 px-5 py-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    isSelected
                      ? 'border-primary bg-primary/10 text-foreground shadow-sm shadow-primary/10'
                      : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50 hover:shadow-sm'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all',
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/30 group-hover:border-primary/50'
                    )}>
                      {isSelected && (
                        <div className="h-2.5 w-2.5 rounded-full bg-primary-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <span className={cn(
                        'font-medium block',
                        isSelected ? 'text-foreground' : 'text-foreground'
                      )}>
                        {option.text}
                      </span>
                      {option.description && (
                        <p className={cn(
                          'text-sm leading-relaxed',
                          isSelected ? 'text-muted-foreground' : 'text-muted-foreground'
                        )}>
                          {option.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );
      case 'DROPDOWN': {
        // DROPDOWN uses Select component for better UX
        // Store/send the backend value (option.value) when present, otherwise fallback to option.id.
        const dropdownValue = typeof value === 'string' && value ? value : undefined;
        return (
          <div className="w-full">
            <Select
              value={dropdownValue}
              onValueChange={(newValue) => {
                // Always allow selection - call onChange with new value
                if (newValue) {
                  onChange(newValue);
                }
              }}
            >
              <SelectTrigger className="w-full h-12 text-base rounded-xl border-2">
                <SelectValue placeholder="Select an option..." />
              </SelectTrigger>
              <SelectContent>
                {question.options?.map((option) => {
                  const storedValue = option.value || option.id;
                  if (!storedValue) {
                    console.warn('[QuestionCard] DROPDOWN option missing both id and value:', option);
                    return null; // Skip invalid options
                  }
                  return (
                    <SelectItem 
                      key={storedValue} 
                      value={storedValue}
                      className="text-base py-3"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{option.text}</span>
                        {option.description && (
                          <span className="text-sm text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        );
      }
      case 'MULTIPLE_CHOICE': {
        // Normalize to array: support [...] or "a,b" (e.g. from API/hydration)
        let selections: string[];
        if (Array.isArray(value)) {
          selections = value.filter((s): s is string => typeof s === 'string' && s.length > 0);
        } else if (typeof value === 'string' && value.trim()) {
          selections = value.split(',').map((s) => s.trim()).filter(Boolean);
        } else {
          selections = [];
        }
        // Debug: log option id/value and current selections
        if (question.options?.length) {
          console.log('[Onboarding] MULTIPLE_CHOICE options (id, value):', question.options.map((o) => ({ id: o.id, value: o.value ?? '(none)' })));
          console.log('[Onboarding] MULTIPLE_CHOICE selections (stored as values):', selections);
        }
        return (
          <div className="space-y-3">
            <div className="grid gap-3">
              {question.options?.map((option) => {
                // Store value when present, else id. Match selection by value, id, or text (e.g. after hydration from API).
                const storedValue = option.value != null && String(option.value).trim() !== ''
                  ? String(option.value).trim()
                  : option.id;
                const optText = option.text != null && String(option.text).trim() !== '' ? String(option.text).trim() : '';
                const isSelected =
                  selections.includes(storedValue) ||
                  selections.includes(option.id) ||
                  (option.value != null && selections.includes(option.value)) ||
                  (optText.length > 0 && selections.includes(optText));
                const isDisabled =
                  !isSelected &&
                  maxSelections !== undefined &&
                  selections.length >= maxSelections;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        onChange(
                          selections.filter(
                            (item) =>
                              item !== storedValue &&
                              item !== option.id &&
                              item !== option.value &&
                              (optText.length === 0 || item !== optText)
                          )
                        );
                      } else if (!isDisabled) {
                        onChange([...selections, storedValue]);
                      }
                    }}
                    disabled={isDisabled}
                    className={cn(
                      'group relative w-full rounded-xl border-2 px-5 py-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                      isSelected
                        ? 'border-primary bg-primary/10 text-foreground shadow-sm shadow-primary/10'
                        : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50 hover:shadow-sm',
                      isDisabled && 'cursor-not-allowed opacity-50 hover:border-border hover:bg-card'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all',
                        isSelected
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground/30 group-hover:border-primary/50'
                      )}>
                        {isSelected && (
                          <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 space-y-1">
                        <span className="font-medium block text-foreground">
                          {option.text}
                        </span>
                        {option.description && (
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {option.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {(maxSelections || minSelections) && (
              <p className="text-sm text-muted-foreground px-1">
                {minSelections && maxSelections
                  ? `Select between ${minSelections} and ${maxSelections} option${maxSelections > 1 ? 's' : ''}`
                  : maxSelections
                  ? `Select up to ${maxSelections} option${maxSelections > 1 ? 's' : ''}`
                  : minSelections
                  ? `Select at least ${minSelections} option${minSelections > 1 ? 's' : ''}`
                  : ''}
              </p>
            )}
          </div>
        );
      }
      default:
        return (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
            Question type <span className="font-semibold">{question.type}</span>{' '}
            is not yet supported.
          </div>
        );
    }
  };

  // HEADING type - render as a heading on the page
  // Type assertion needed because TypeScript doesn't narrow union types properly in this context
  if ((question.type as string) === 'HEADING') {
    return (
      <div className="space-y-4 py-6">
        {question.section?.heading && (
          <div className="inline-flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              {question.section.heading}
            </span>
          </div>
        )}
        <h2 className="flex items-start gap-4 text-3xl sm:text-4xl font-bold leading-tight text-foreground tracking-tight">
          {emoji && (
            <span 
              className="text-3xl sm:text-4xl leading-none flex-shrink-0 select-none" 
              role="img" 
              aria-hidden="true"
            >
              {emoji}
            </span>
          )}
          <span className="flex-1">
            {cleanText || question.heading?.heading || question.text}
          </span>
        </h2>
        {question.heading?.shortHeading && (
          <p className="text-lg text-muted-foreground">
            {question.heading.shortHeading}
          </p>
        )}
        {question.text && question.heading?.heading !== question.text && (
          <p className="text-base text-muted-foreground/80">
            {question.text}
          </p>
        )}
      </div>
    );
  }

  // BANNER type - should be handled separately in OnboardingPage (overlay)
  // This component should not render for BANNER
  // Type assertion needed because TypeScript doesn't narrow union types properly in this context
  if ((question.type as string) === 'BANNER') {
    return null;
  }

  return (
    <Card className="shadow-xl border-border/50 bg-card/95 backdrop-blur-sm overflow-hidden">
      <CardHeader className="space-y-4 pb-6">
        {question.heading?.heading && !emoji && (
          <div className="inline-flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary px-2.5 py-1 rounded-full bg-primary/10">
              {question.heading.heading}
            </span>
          </div>
        )}
        <CardTitle className="flex items-start gap-4 text-4xl font-bold leading-[40px] text-foreground tracking-tight">
          {emoji && (
            <span 
              className="text-4xl sm:text-5xl leading-none flex-shrink-0 select-none" 
              role="img" 
              aria-hidden="true"
              style={{ lineHeight: '1' }}
            >
              {emoji}
            </span>
          )}
          <span className="flex-1 leading-[40px]">
            {cleanText}
            {question.required && (
              <span className="ml-2 text-destructive align-top" title="Required">*</span>
            )}
          </span>
        </CardTitle>
        {question.heading?.shortHeading && (
          <CardDescription className="text-base text-muted-foreground pl-0">
            {question.heading.shortHeading}
          </CardDescription>
        )}
        {(() => {
          const helpText = question.metadata?.helpText;
          return typeof helpText === 'string' && helpText ? (
            <CardDescription className="text-sm text-muted-foreground leading-relaxed pl-0">
              {helpText}
            </CardDescription>
          ) : null;
        })()}
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {renderInput()}
        {question.validationRules?.maxLength && question.type.startsWith('TEXT') && (
          <p className="text-xs text-muted-foreground">
            Maximum {question.validationRules.maxLength} characters
          </p>
        )}
        {question.section?.heading && (
          <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {question.section.heading}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

